import { useState, useEffect } from 'react';
import { Check, Users, Heart, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const commitments = [
  { id: 'growth', label: 'Personal Growth', icon: Sparkles, description: 'I commit to continuous self-improvement' },
  { id: 'brotherhood', label: 'Brotherhood', icon: Users, description: 'I will support and uplift my brothers' },
  { id: 'legacy', label: 'Building Legacy', icon: Heart, description: 'I will create lasting impact for future generations' }
];

export const BrotherhoodPledge = () => {
  const [selectedCommitment, setSelectedCommitment] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [pledgeCount, setPledgeCount] = useState(0);
  const [recentPledges, setRecentPledges] = useState([]);

  // Fetch pledge count and recent pledges on mount
  useEffect(() => {
    const fetchPledgeData = async () => {
      try {
        const [countRes, recentRes] = await Promise.all([
          fetch(`${API}/pledge/count`),
          fetch(`${API}/pledge/recent`)
        ]);
        
        if (countRes.ok) {
          const countData = await countRes.json();
          setPledgeCount(countData.count);
        }
        
        if (recentRes.ok) {
          const recentData = await recentRes.json();
          setRecentPledges(recentData);
        }
      } catch (error) {
        console.error('Error fetching pledge data:', error);
      }
    };

    fetchPledgeData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCommitment || !name || !email) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/pledge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          commitment: selectedCommitment,
          pledge_text: "I pledge to rise as a king, support my brothers, and build a lasting legacy."
        })
      });

      if (response.ok) {
        setHasSubmitted(true);
        setPledgeCount(prev => prev + 1);
        toast.success('Your pledge has been recorded, King!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit pledge');
      }
    } catch (error) {
      console.error('Error submitting pledge:', error);
      toast.error('Failed to submit pledge. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (hasSubmitted) {
    return (
      <section 
        id="pledge" 
        className="py-24 px-6 bg-[#0A0A0A]"
        data-testid="brotherhood-pledge-section"
      >
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-20 h-20 rounded-full bg-[#D4AF37]/20 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-[#D4AF37]" />
          </div>
          <h2 className="font-cormorant text-4xl font-medium text-white mb-4">
            Welcome to the Brotherhood, {name.split(' ')[0]}
          </h2>
          <p className="text-zinc-400 font-manrope text-lg mb-8">
            You've joined <span className="text-[#D4AF37] font-semibold">{pledgeCount} brothers</span> who have committed to rising as kings.
          </p>
          <a
            href="#pricing"
            className="inline-block bg-[#D4AF37] text-[#0A0A0A] px-10 py-4 rounded-full font-manrope font-bold text-lg hover:bg-[#B8860B] transition-all hover:-translate-y-1 gold-glow"
            data-testid="pledge-complete-cta"
          >
            Secure Your Spot at The Reset
          </a>
        </div>
      </section>
    );
  }

  return (
    <section 
      id="pledge" 
      className="py-24 px-6 bg-[#0A0A0A] relative overflow-hidden"
      data-testid="brotherhood-pledge-section"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/4 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.2em] text-xs font-bold mb-4">
            Join the Movement
          </p>
          <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white mb-4">
            Take the Brotherhood Pledge
          </h2>
          <p className="text-zinc-400 font-manrope max-w-2xl mx-auto">
            Before the retreat, commit to your transformation. Join {pledgeCount > 0 ? `${pledgeCount}+ brothers` : 'other brothers'} who have already taken the pledge.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Pledge Form */}
          <div className="bg-[#111111] border border-white/10 rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Commitment Selection */}
              <div>
                <label className="block text-zinc-400 font-manrope text-sm mb-4">
                  What do you commit to?
                </label>
                <div className="space-y-3">
                  {commitments.map((commitment) => {
                    const Icon = commitment.icon;
                    const isSelected = selectedCommitment === commitment.id;
                    
                    return (
                      <button
                        key={commitment.id}
                        type="button"
                        onClick={() => setSelectedCommitment(commitment.id)}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left ${
                          isSelected 
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10' 
                            : 'border-white/10 hover:border-white/20'
                        }`}
                        data-testid={`commitment-${commitment.id}`}
                      >
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          isSelected ? 'bg-[#D4AF37]/20' : 'bg-white/5'
                        }`}>
                          <Icon className={`w-6 h-6 ${isSelected ? 'text-[#D4AF37]' : 'text-zinc-500'}`} />
                        </div>
                        <div className="flex-1">
                          <p className={`font-manrope font-medium ${isSelected ? 'text-white' : 'text-zinc-300'}`}>
                            {commitment.label}
                          </p>
                          <p className="text-zinc-500 font-manrope text-sm">
                            {commitment.description}
                          </p>
                        </div>
                        {isSelected && (
                          <Check className="w-5 h-5 text-[#D4AF37]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Name Input */}
              <div>
                <label className="block text-zinc-400 font-manrope text-sm mb-2">
                  Your Name
                </label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your full name"
                  className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="pledge-name-input"
                  required
                />
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-zinc-400 font-manrope text-sm mb-2">
                  Your Email
                </label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="bg-[#0A0A0A] border-white/10 text-white placeholder:text-zinc-600"
                  data-testid="pledge-email-input"
                  required
                />
              </div>

              {/* The Pledge Text */}
              <div className="p-4 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl">
                <p className="text-zinc-300 font-manrope text-sm italic text-center">
                  "I pledge to rise as a king, support my brothers, and build a lasting legacy."
                </p>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting || !selectedCommitment}
                className="w-full bg-[#D4AF37] text-[#0A0A0A] py-4 rounded-xl font-manrope font-bold text-lg hover:bg-[#B8860B] transition-all disabled:opacity-50"
                data-testid="pledge-submit-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Take the Pledge'}
              </Button>
            </form>
          </div>

          {/* Social Proof - Recent Pledges */}
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-6 text-center">
              <div className="text-5xl font-cormorant font-medium text-[#D4AF37] mb-2">
                {pledgeCount}+
              </div>
              <p className="text-zinc-400 font-manrope">
                Brothers have taken the pledge
              </p>
            </div>

            {/* Recent pledges */}
            <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
              <h3 className="font-manrope font-semibold text-white mb-4">
                Recent Pledges
              </h3>
              <div className="space-y-4">
                {recentPledges.length > 0 ? (
                  recentPledges.slice(0, 5).map((pledge, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6239] flex items-center justify-center">
                        <span className="text-[#0A0A0A] font-manrope font-bold text-sm">
                          {pledge.display_name?.charAt(0) || 'B'}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-manrope text-sm">
                          {pledge.display_name || 'A Brother'}
                        </p>
                        <p className="text-zinc-500 font-manrope text-xs">
                          Committed to {pledge.commitment || 'the brotherhood'}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  // Placeholder recent pledges
                  ['Marcus J.', 'Terrence W.', 'David H.', 'Kevin M.', 'James T.'].map((name, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#8C6239] flex items-center justify-center">
                        <span className="text-[#0A0A0A] font-manrope font-bold text-sm">
                          {name.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-manrope text-sm">{name}</p>
                        <p className="text-zinc-500 font-manrope text-xs">
                          Committed to brotherhood
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Quote */}
            <div className="bg-[#111111] border border-[#D4AF37]/20 rounded-2xl p-6">
              <p className="text-zinc-300 font-manrope italic">
                "When one brother rises, he reaches back to pull others up. When one brother struggles, the brotherhood surrounds him with support."
              </p>
              <p className="text-[#D4AF37] font-manrope text-sm mt-3">
                — The Reset Brotherhood
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
